/* eslint-disable react/prop-types */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { purchaseNativeStoreProduct } from "../../native/nativeStore";
import useNativeStoreCatalog from "../../native/useNativeStoreCatalog";
import EventCard from "./EventCard";
import AppModalShell from "../common/AppModalShell";

const sampleEvents = [
  {
    id: "demo-running",
    name: "Running Club",
    event_format: 1,
    host_format: 0,
    fee_type: 0,
    is_verified: false,
    date_time: "2026-05-02T18:00:00",
    color: "#D8E6FF",
  },
  {
    id: "demo-badminton",
    name: "Badminton",
    event_format: 1,
    host_format: 0,
    fee_type: 1,
    is_verified: false,
    date_time: "2026-05-03T15:00:00",
    color: "#E6F6D8",
  },
  {
    id: "demo-workshop",
    name: "Design Workshop",
    event_format: 0,
    host_format: 1,
    fee_type: 0,
    is_verified: true,
    date_time: "2026-05-04T17:30:00",
    color: "#F7DCE8",
  },
  {
    id: "demo-cold",
    name: "Cold Bathing",
    event_format: 1,
    host_format: 0,
    fee_type: 0,
    is_verified: false,
    date_time: "2026-05-05T10:00:00",
    color: "#DCF7F4",
  },
];

const normalizeDemoEvent = (event) => ({
  id: event?.id || "current-event",
  name: event?.name || "Your Event",
  type: event?.type || "event",
  event_format: Number(event?.event_format ?? 1),
  host_format: Number(event?.host_format ?? 0),
  fee_type: Number(event?.fee_type ?? 0),
  is_verified: Boolean(event?.is_verified),
  date_time: event?.date_time || new Date().toISOString(),
  badge_label: event?.badge_label || null,
  icon_image_url: event?.icon_image_url || null,
  color: "#FFFFFF",
  isCurrent: true,
});

const formatEventDate = (dateTime) => dateTime || new Date().toISOString();

const BoostDemoCard = ({ event, promoted = false }) => (
  <div className="relative z-10 py-1">
    <EventCard
      title={event.name}
      type={event.type}
      isOnline={Number(event.event_format) === 0}
      isPersonal={Number(event.host_format) === 0}
      isPaid={Number(event.fee_type) === 1}
      minVaultLevel={event.min_vault_level}
      isVerified={event.is_verified}
      datetime={formatEventDate(event.date_time)}
      isFavourite={false}
      onToggleFavourite={() => {}}
      badgeLabel={event.badge_label}
      iconImageUrl={event.icon_image_url}
      color={event.color}
      isPromoted={promoted}
      showFavouriteButton={false}
      titleMaxWidthClass="max-w-[180px] sm:max-w-[210px]"
      featuredBadgeClassName="bg-[#071A2D]"
    />
  </div>
);

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const SlideToPreview = ({ disabled = false, onComplete }) => {
  const { t } = useTranslation("events");
  const trackRef = React.useRef(null);
  const completedRef = React.useRef(false);
  const [progress, setProgress] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);

  React.useEffect(() => {
    completedRef.current = false;
    setProgress(0);
    setDragging(false);
  }, [disabled]);

  const complete = React.useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    setDragging(false);
    setProgress(1);
    window.setTimeout(() => onComplete?.(), 160);
  }, [onComplete]);

  const updateProgress = React.useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const knob = 44;
    const padding = 4;
    const travel = Math.max(1, rect.width - knob - padding * 2);
    const next = clamp((clientX - rect.left - knob / 2 - padding) / travel, 0, 1);
    setProgress(next);
    return next;
  }, []);

  const handlePointerDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    completedRef.current = false;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateProgress(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (!dragging || disabled) return;
    const next = updateProgress(e.clientX);
    if (next >= 0.94) complete();
  };

  const handlePointerUp = () => {
    if (!dragging || disabled) return;
    setDragging(false);
    if (progress >= 0.82) {
      complete();
      return;
    }
    setProgress(0);
  };

  return (
    <div
      ref={trackRef}
      className={`relative mx-auto mt-4 h-12 w-[230px] select-none overflow-hidden rounded-2xl border border-white/15 bg-white/10 p-1 ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      role="button"
      aria-disabled={disabled}
      aria-label={t("events.details.boost_modal.slide_aria")}
      style={{ touchAction: "none" }}
    >
      <div
        className="absolute inset-y-1 left-1 rounded-xl bg-[#0073FF]/75 transition-[width] duration-150"
        style={{ width: `${8 + progress * 88}%` }}
        aria-hidden="true"
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-extrabold text-white/90">
        {t("events.details.boost_modal.slide")}
      </span>
      <span
        className="pointer-events-none absolute left-1 top-1 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#0073FF] shadow-lg transition-transform duration-150"
        style={{ transform: `translateX(${progress * 178}px)` }}
        aria-hidden="true"
      >
        <img
          src="/assets/events/boost_icon.svg"
          alt=""
          className="pointer-events-none h-5 w-5"
          draggable={false}
        />
      </span>
    </div>
  );
};

const DiamondIcon = () => (
  <span
    className="h-4 w-4 bg-[#0073FF]"
    style={{
      WebkitMask: "url('/assets/vault/diamand_icon.svg') center / contain no-repeat",
      mask: "url('/assets/vault/diamand_icon.svg') center / contain no-repeat",
    }}
    aria-hidden="true"
  />
);

const DIAMONDS_SHOP_PATH = "/shop/catalog?category=diamonds";

const normalizeDurationOptions = (source, prev = {}) => {
  const rawOptions =
    source?.duration_options ??
    source?.boost_duration_options ??
    prev.durationOptions;

  if (Array.isArray(rawOptions) && rawOptions.length) {
    const normalized = rawOptions
      .map((option) => ({
        days: Number(option?.days ?? option?.duration_days),
        diamondCost: Number(option?.diamond_cost ?? option?.cost ?? 0),
      }))
      .filter((option) => Number.isFinite(option.days) && option.days > 0);

    if (normalized.length) {
      return normalized.sort((a, b) => a.days - b.days);
    }
  }

  const fallbackDays = Number(
    source?.duration_days ??
      source?.boost_duration_days ??
      prev.defaultPaidDurationDays ??
      3
  ) || 3;
  const fallbackCost = Number(
    source?.diamond_cost ??
      source?.boost_diamond_cost ??
      prev.diamondCost ??
      0
  ) || 0;

  return [{ days: fallbackDays, diamondCost: fallbackCost }];
};

const buildBoostMeta = (source, prev = {}) => ({
  diamondCost: Number(source?.diamond_cost ?? source?.boost_diamond_cost ?? prev.diamondCost ?? 0) || 0,
  defaultPaidDurationDays: Number(
    source?.duration_days ??
      source?.boost_duration_days ??
      prev.defaultPaidDurationDays ??
      3
  ) || 3,
  durationOptions: normalizeDurationOptions(source, prev),
  diamondsBalance: Number(source?.diamonds_balance ?? prev.diamondsBalance ?? 0) || 0,
  premiumFreeAvailable: Boolean(
    source?.premium_free_available ?? source?.boost_premium_free_available ?? prev.premiumFreeAvailable
  ),
  premiumFreeUsed: Boolean(
    source?.premium_free_used ?? source?.boost_premium_free_used ?? prev.premiumFreeUsed
  ),
  hasPremium: Boolean(source?.has_premium_subscription ?? prev.hasPremium),
  freeDurationDays: Number(
    source?.premium_free_duration_days
      ?? source?.boost_premium_free_duration_days
      ?? prev.freeDurationDays
      ?? 3
  ) || 3,
  isPromotedActive: Boolean(source?.is_promoted_active ?? prev.isPromotedActive ?? source?.promoted),
});

const BoostEventModal = ({
  event,
  isOpen,
  onClose,
  onBoosted = () => {},
  skipLabel = "",
  onSkip,
  onCreateWithBoost,
  fetchBoostMeta,
  autoCloseAfterSuccessMs = 0,
  announceSuccessToast = true,
  embedded = false,
}) => {
  const { t } = useTranslation("events");
  const navigate = useNavigate();
  const isPreCreateMode = Boolean(onCreateWithBoost);
  const {
    nativeApp,
    products: nativeStoreProducts,
    ready: nativeStoreReady,
  } = useNativeStoreCatalog(isOpen);
  const boostMetaSource = React.useMemo(
    () => ({
      id: event?.id,
      boost_diamond_cost: event?.boost_diamond_cost,
      boost_duration_days: event?.boost_duration_days,
      boost_duration_options: event?.boost_duration_options,
      duration_days: event?.duration_days,
      duration_options: event?.duration_options,
      boost_premium_free_available: event?.boost_premium_free_available,
      boost_premium_free_duration_days: event?.boost_premium_free_duration_days,
      boost_premium_free_used: event?.boost_premium_free_used,
      diamonds_balance: event?.diamonds_balance,
      has_premium_subscription: event?.has_premium_subscription,
      is_promoted_active: event?.is_promoted_active,
      promoted: event?.promoted,
    }),
    [
      event?.boost_diamond_cost,
      event?.boost_duration_days,
      event?.boost_duration_options,
      event?.boost_premium_free_available,
      event?.boost_premium_free_duration_days,
      event?.boost_premium_free_used,
      event?.diamonds_balance,
      event?.duration_days,
      event?.duration_options,
      event?.has_premium_subscription,
      event?.id,
      event?.is_promoted_active,
      event?.promoted,
    ]
  );
  const initialBoostMeta = React.useMemo(
    () => buildBoostMeta(boostMetaSource),
    [boostMetaSource]
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [previewBoosted, setPreviewBoosted] = React.useState(false);
  const [localBoosted, setLocalBoosted] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState("");
  const [inlineError, setInlineError] = React.useState("");
  const [showDiamondsShopCta, setShowDiamondsShopCta] = React.useState(false);
  const [skipSubmitting, setSkipSubmitting] = React.useState(false);
  const [boostMeta, setBoostMeta] = React.useState(initialBoostMeta);
  const [successPayload, setSuccessPayload] = React.useState(null);
  const boostMetaRef = React.useRef(initialBoostMeta);
  const wasOpenRef = React.useRef(false);
  const [selectedDurationDays, setSelectedDurationDays] = React.useState(
    initialBoostMeta.defaultPaidDurationDays
  );

  const alreadyBoosted = Boolean(boostMeta.isPromotedActive);
  const actualBoosted = localBoosted || alreadyBoosted;
  const durationOptions = boostMeta.durationOptions || [];
  const selectedDurationOption =
    durationOptions.find((option) => Number(option.days) === Number(selectedDurationDays)) ||
    durationOptions[0] ||
    { days: boostMeta.defaultPaidDurationDays, diamondCost: boostMeta.diamondCost };
  const selectedDiamondCost = Number(selectedDurationOption?.diamondCost ?? boostMeta.diamondCost ?? 0) || 0;
  const diamondCost = selectedDiamondCost;
  const diamondsBalance = boostMeta.diamondsBalance;
  const premiumFreeAvailable = boostMeta.premiumFreeAvailable;
  const premiumFreeUsed = boostMeta.premiumFreeUsed;
  const hasPremium = boostMeta.hasPremium;
  const freeDurationDays = boostMeta.freeDurationDays;
  const nativeBoostProduct = nativeStoreProducts.find(
    (product) =>
      product.kind === "event_boost" &&
      Number(product.boost_duration_days) === Number(selectedDurationDays) &&
      product.store_available
  );
  const nativePaidMode = nativeApp && !premiumFreeAvailable;
  const showSuccessState = Boolean(successPayload) && actualBoosted;
  const eventIsReady = isPreCreateMode
    ? true
    : Boolean(event?.id) &&
      event?.status !== "deleted" &&
      event?.status !== "finished" &&
      event?.status !== "pending" &&
      event?.is_validated !== false;

  React.useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setSubmitting(false);
    setLocalBoosted(alreadyBoosted);
    setPreviewBoosted(alreadyBoosted);
    setConfirmAction("");
    setInlineError("");
    setShowDiamondsShopCta(false);
    setSkipSubmitting(false);
    setSuccessPayload(null);
    boostMetaRef.current = initialBoostMeta;
    setBoostMeta(initialBoostMeta);
    setSelectedDurationDays(initialBoostMeta.defaultPaidDurationDays);
  }, [alreadyBoosted, initialBoostMeta, isOpen]);

  React.useEffect(() => {
    if (!actualBoosted || !autoCloseAfterSuccessMs || !successPayload) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      onBoosted(successPayload);
    }, autoCloseAfterSuccessMs);
    return () => window.clearTimeout(timeoutId);
  }, [actualBoosted, autoCloseAfterSuccessMs, onBoosted, successPayload]);

  React.useEffect(() => {
    if (!confirmAction) return undefined;
    const timeoutId = window.setTimeout(() => {
      setConfirmAction("");
    }, 4500);
    return () => window.clearTimeout(timeoutId);
  }, [confirmAction]);

  const previousDurationDaysRef = React.useRef(selectedDurationDays);
  React.useEffect(() => {
    if (previousDurationDaysRef.current === selectedDurationDays) return;
    previousDurationDaysRef.current = selectedDurationDays;
    if (confirmAction === "diamonds" || confirmAction === "native") {
      setConfirmAction("");
      setInlineError("");
      setShowDiamondsShopCta(false);
    }
  }, [confirmAction, selectedDurationDays]);

  const currentEvent = React.useMemo(() => normalizeDemoEvent(event), [event]);
  const beforeEvents = React.useMemo(
    () => [
      sampleEvents[0],
      sampleEvents[1],
      currentEvent,
      sampleEvents[2],
      sampleEvents[3],
      sampleEvents[0],
      currentEvent,
      sampleEvents[1],
    ],
    [currentEvent]
  );
  const afterEvents = React.useMemo(
    () => [sampleEvents[1], sampleEvents[2], sampleEvents[3], sampleEvents[0]],
    []
  );

  const resetActionState = React.useCallback(() => {
    setConfirmAction("");
    setInlineError("");
    setShowDiamondsShopCta(false);
  }, []);

  const syncBoostMeta = React.useCallback((source) => {
    const nextMeta = buildBoostMeta(source, boostMetaRef.current);
    boostMetaRef.current = nextMeta;
    setBoostMeta(nextMeta);
    setSelectedDurationDays((current) => {
      const hasCurrent = nextMeta.durationOptions?.some(
        (option) => Number(option.days) === Number(current)
      );
      return hasCurrent ? current : nextMeta.defaultPaidDurationDays;
    });
    return nextMeta;
  }, []);

  const refreshBoostState = React.useCallback(async () => {
    if (fetchBoostMeta) {
      const data = await fetchBoostMeta();
      return syncBoostMeta(data);
    }
    if (!event?.id) return boostMetaRef.current;
    const { data } = await api.get(`/events/${event.id}/boost/`);
    return syncBoostMeta(data);
  }, [event?.id, fetchBoostMeta, syncBoostMeta]);

  const handleSubmit = React.useCallback(async () => {
    if (submitting || actualBoosted || !eventIsReady || !previewBoosted) return;
    if (!event?.id && !isPreCreateMode) return;
    resetActionState();
    setSuccessPayload(null);
    setSubmitting(true);
    try {
      let nativeStoreTransactionId = "";
      if (nativePaidMode) {
        if (!nativeBoostProduct) {
          setInlineError(t("events.details.boost_modal.native_unavailable"));
          return;
        }
        const purchase = await purchaseNativeStoreProduct(
          nativeBoostProduct.store_product_id
        );
        nativeStoreTransactionId = purchase?.delivery?.store_transaction_id || "";
        if (!nativeStoreTransactionId) {
          throw new Error(t("events.details.boost_modal.native_verification_failed"));
        }
      }
      const data = isPreCreateMode
        ? await onCreateWithBoost?.({
            durationDays: selectedDurationDays,
            nativeStoreTransactionId,
          })
        : (
            await api.post(`/events/${event.id}/boost/`, {
              duration_days: selectedDurationDays,
              ...(nativeStoreTransactionId
                ? { native_store_transaction_id: nativeStoreTransactionId }
                : {}),
            })
          ).data;
      if (!data) return;
      syncBoostMeta(data);
      setLocalBoosted(true);
      setPreviewBoosted(true);
      setSuccessPayload(data);
      if (!autoCloseAfterSuccessMs) {
        onBoosted(data);
      }
      if (announceSuccessToast) {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("events.details.boost_modal.success"),
            },
          })
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        syncBoostMeta(err?.response?.data || {});
        setInlineError(
          t("events.details.boost_modal.not_enough_diamonds_inline", {
            cost: diamondCost,
          })
        );
        setShowDiamondsShopCta(true);
        return;
      }
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        (nativePaidMode
          ? t("events.details.boost_modal.native_verification_failed")
          : t("events.details.boost_modal.failed"));
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message: msg, status },
        })
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    actualBoosted,
    announceSuccessToast,
    autoCloseAfterSuccessMs,
    diamondCost,
    event?.id,
    eventIsReady,
    isPreCreateMode,
    nativeBoostProduct,
    nativePaidMode,
    onCreateWithBoost,
    onBoosted,
    previewBoosted,
    resetActionState,
    selectedDurationDays,
    syncBoostMeta,
    submitting,
    t,
  ]);

  if (!isOpen) return null;

  const titleKey =
    event?.type === "concept"
      ? "events.details.boost_modal.title_concept"
      : "events.details.boost_modal.title";
  const disabledReason = !eventIsReady
    ? t("events.details.boost_modal.unavailable")
    : "";
  const showPaymentButton = previewBoosted && !actualBoosted;
  const paymentDisabled =
    submitting ||
    actualBoosted ||
    !eventIsReady ||
    (nativePaidMode && !nativeStoreReady);
  const requiresDiamondPayment = !premiumFreeAvailable;
  const premiumHintKey = hasPremium
    ? premiumFreeAvailable
      ? "events.details.boost_modal.premium_available"
      : premiumFreeUsed
        ? "events.details.boost_modal.premium_used"
        : "events.details.boost_modal.premium_active"
    : "";

  const openPremium = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/menu/subscription?src=event_boost");
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const openDiamondsShop = () => {
    navigate(DIAMONDS_SHOP_PATH);
    handleClose();
  };

  const handleSkipClick = async () => {
    if (!onSkip || submitting) return;
    resetActionState();
    setSuccessPayload(null);
    setSkipSubmitting(true);
    setSubmitting(true);
    try {
      await onSkip();
    } finally {
      setSkipSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleActionClick = async () => {
    if (paymentDisabled || !showPaymentButton) return;

    let freshMeta = boostMetaRef.current;
    try {
      freshMeta = await refreshBoostState();
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        t("events.details.boost_modal.failed");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message },
        })
      );
      return;
    }

    if (freshMeta.isPromotedActive) {
      setLocalBoosted(true);
      setPreviewBoosted(true);
      setSuccessPayload(null);
      resetActionState();
      return;
    }

    if (freshMeta.premiumFreeAvailable) {
      if (confirmAction === "premium") {
        void handleSubmit();
        return;
      }
      setInlineError("");
      setShowDiamondsShopCta(false);
      setConfirmAction("premium");
      return;
    }

    if (nativeApp) {
      if (!nativeBoostProduct) {
        setConfirmAction("");
        setInlineError(t("events.details.boost_modal.native_unavailable"));
        setShowDiamondsShopCta(false);
        return;
      }
      if (confirmAction === "native") {
        void handleSubmit();
        return;
      }
      setInlineError("");
      setShowDiamondsShopCta(false);
      setConfirmAction("native");
      return;
    }

    const freshDurationOption =
      freshMeta.durationOptions?.find(
        (option) => Number(option.days) === Number(selectedDurationDays)
      ) ||
      freshMeta.durationOptions?.[0] ||
      { diamondCost: freshMeta.diamondCost };
    const freshDiamondCost = Number(freshDurationOption?.diamondCost ?? freshMeta.diamondCost ?? 0) || 0;
    const enoughDiamonds = freshMeta.diamondsBalance >= freshDiamondCost;
    if (!enoughDiamonds) {
      setConfirmAction("");
      setInlineError(
        t("events.details.boost_modal.not_enough_diamonds_inline", {
          cost: freshDiamondCost,
        })
      );
      setShowDiamondsShopCta(true);
      return;
    }

    if (confirmAction === "diamonds") {
      void handleSubmit();
      return;
    }

    setInlineError("");
    setShowDiamondsShopCta(false);
    setConfirmAction("diamonds");
  };

  const insufficientBalanceText = showDiamondsShopCta
    ? t("events.details.boost_modal.current_balance", {
        balance: diamondsBalance,
      })
    : "";

  const confirmHint =
    confirmAction === "premium"
      ? t("events.details.boost_modal.premium_confirm_hint", {
          days: freeDurationDays,
        })
      : confirmAction === "diamonds"
        ? t("events.details.boost_modal.diamonds_confirm_hint", {
            cost: diamondCost,
            days: selectedDurationOption.days,
          })
        : confirmAction === "native"
          ? t("events.details.boost_modal.native_confirm_hint", {
              price: nativeBoostProduct?.localized_price || "",
              days: selectedDurationOption.days,
            })
        : "";
  const diamondButtonLabel = submitting
    ? t("events.details.boost_modal.boosting")
    : confirmAction === "diamonds" || confirmAction === "native"
      ? t("events.details.boost_modal.confirm_button")
      : t("events.details.boost_modal.boost");
  const premiumButtonLabel = submitting
    ? t("events.details.boost_modal.boosting")
    : confirmAction === "premium"
      ? t("events.details.boost_modal.confirm_question")
      : t("events.details.boost_modal.premium_button");
  const skipButtonLabel = skipSubmitting
    ? t("events.details.boost_modal.creating_event")
    : skipLabel || t("events.details.boost_modal.continue_without_boost");

  const bodyContent = (
    <>
      <style>
        {`
          @keyframes vgBoostScroll {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          @keyframes vgBoostPin {
            0% { transform: translateY(82px) scale(0.98); opacity: 0.25; }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }
          .vg-boost-scroll-track {
            animation: vgBoostScroll 9s linear infinite;
          }
          .vg-boost-scroll-track-slow {
            animation: vgBoostScroll 11s linear infinite;
          }
          .vg-boost-pin {
            animation: vgBoostPin 440ms ease-out both;
          }
          @media (prefers-reduced-motion: reduce) {
            .vg-boost-scroll-track,
            .vg-boost-scroll-track-slow,
            .vg-boost-pin {
              animation: none;
            }
          }
        `}
      </style>

          <div className="rounded-2xl border border-white/10 bg-black/15 p-2 transition-all duration-300">
            {!previewBoosted ? (
              <div className="transition-opacity duration-300">
                <div className="mb-1 px-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-white/40">
                  {t("events.details.boost_modal.before")}
                </div>
                <div className="relative h-[316px] overflow-hidden rounded-xl">
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-10 bg-gradient-to-b from-[#071A2D] to-transparent" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-10 bg-gradient-to-t from-[#071A2D] to-transparent" />
                  <div className="vg-boost-scroll-track flex flex-col gap-1">
                    {[...beforeEvents, ...beforeEvents].map((item, index) => (
                      <BoostDemoCard
                        key={`${item.id}-${index}`}
                        event={item}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="transition-opacity duration-300">
                <div className="mb-2 px-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-white/40">
                  {t("events.details.boost_modal.after")}
                </div>
                <div className="vg-boost-pin relative overflow-visible pt-2">
                  <BoostDemoCard event={currentEvent} promoted />
                </div>
                <div className="relative mt-2 h-[228px] overflow-hidden rounded-xl">
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-8 bg-gradient-to-b from-[#071A2D] to-transparent" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-8 bg-gradient-to-t from-[#071A2D] to-transparent" />
                  <div className="vg-boost-scroll-track-slow flex flex-col gap-1">
                    {[...afterEvents, ...afterEvents, ...afterEvents].map((item, index) => (
                      <BoostDemoCard
                        key={`${item.id}-${index}`}
                        event={item}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {disabledReason ? (
            <p className="mt-3 text-center text-xs font-semibold text-white/55">
              {disabledReason}
            </p>
          ) : null}

          {showSuccessState ? (
            <div className="mx-auto mt-3 max-w-[300px] rounded-2xl border border-emerald-300/30 bg-emerald-500/15 px-4 py-3 text-center">
              <p className="text-sm font-extrabold text-emerald-200">
                {t("events.details.boost_modal.success")}
              </p>
            </div>
          ) : null}

          {!previewBoosted && !actualBoosted ? (
            <SlideToPreview
              disabled={!eventIsReady}
              onComplete={() => setPreviewBoosted(true)}
            />
          ) : null}

          {showPaymentButton ? (
            requiresDiamondPayment ? (
              <>
                {durationOptions.length > 1 ? (
                  <div className="mx-auto mt-4 max-w-[300px]">
                    <div className="mb-2 text-center text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/45">
                      {t("events.details.boost_modal.duration_label")}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {durationOptions.map((option) => {
                        const isSelected = Number(option.days) === Number(selectedDurationDays);
                        const nativeOptionProduct = nativeApp
                          ? nativeStoreProducts.find(
                              (product) =>
                                product.kind === "event_boost" &&
                                Number(product.boost_duration_days) === Number(option.days) &&
                                product.store_available
                            )
                          : null;
                        return (
                          <button
                            key={option.days}
                            type="button"
                            className={`rounded-xl border px-2 py-2 text-center transition ${
                              isSelected
                                ? "border-[#7DBDFF] bg-[#0F6EF0]/30 text-white shadow-[0_0_18px_rgba(0,115,255,0.28)]"
                                : "border-white/12 bg-white/[0.06] text-white/70"
                            }`}
                            onClick={() => setSelectedDurationDays(option.days)}
                          >
                            <span className="block text-sm font-extrabold">
                              {t("events.details.boost_modal.duration_days", {
                                count: option.days,
                              })}
                            </span>
                            <span className="mt-1 flex items-center justify-center gap-1 text-xs font-bold text-white/75">
                              {nativeApp ? (
                                nativeOptionProduct?.localized_price || "—"
                              ) : (
                                <>
                                  <DiamondIcon />
                                  {option.diamondCost}
                                </>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              <button
                type="button"
                className={`mx-auto mt-4 grid h-12 w-[202px] grid-cols-[78px_1px_1fr] items-center overflow-hidden rounded-2xl bg-white text-sm font-extrabold shadow-[0_12px_28px_rgba(0,0,0,0.22)] ${
                  paymentDisabled
                    ? "cursor-default opacity-70"
                    : "active:scale-[0.98]"
                }`}
                onClick={handleActionClick}
                disabled={paymentDisabled}
              >
                <span className="flex items-center justify-center gap-1 text-[#0073FF]">
                  {nativeApp ? (
                    nativeBoostProduct?.localized_price || "—"
                  ) : (
                    <>
                      <DiamondIcon />
                      {diamondCost}
                    </>
                  )}
                </span>
                <span className="h-8 w-px self-center bg-[#D9E6FF]" aria-hidden="true" />
                <span className="text-[#0073FF]">{diamondButtonLabel}</span>
              </button>
              </>
            ) : (
              <button
                type="button"
                className={`mx-auto mt-4 flex h-12 w-[202px] items-center justify-center rounded-2xl bg-white px-4 text-sm font-extrabold text-[#0073FF] shadow-[0_12px_28px_rgba(0,0,0,0.22)] ${
                  paymentDisabled
                    ? "cursor-default opacity-70"
                    : "active:scale-[0.98]"
                }`}
                onClick={handleActionClick}
                disabled={paymentDisabled}
              >
                {premiumButtonLabel}
              </button>
            )
          ) : null}

          {actualBoosted && !showSuccessState ? (
            <p className="mt-4 text-center text-sm font-semibold text-emerald-300">
              {t("events.details.boost_modal.already_boosted")}
            </p>
          ) : null}

          {confirmHint && !actualBoosted ? (
            <p className="mx-auto mt-3 max-w-[300px] text-center text-xs font-medium leading-5 text-white/60">
              {confirmHint}
            </p>
          ) : null}

          {inlineError ? (
            <p className="mx-auto mt-3 max-w-[300px] text-center text-xs font-semibold leading-5 text-red-200">
              {inlineError}
            </p>
          ) : null}

          {insufficientBalanceText ? (
            <p className="mx-auto mt-2 max-w-[300px] text-center text-xs font-medium leading-5 text-white/65">
              {insufficientBalanceText}
            </p>
          ) : null}

          {showDiamondsShopCta && !nativeApp ? (
            <button
              type="button"
              className="mx-auto mt-3 flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-extrabold text-white"
              onClick={openDiamondsShop}
            >
              {t("events.details.boost_modal.open_diamonds_shop")}
            </button>
          ) : null}

          {previewBoosted && !actualBoosted ? (
            <p className="mx-auto mt-3 max-w-[300px] text-center text-xs font-medium leading-5 text-white/55">
              {!hasPremium ? (
                <>
                  <button
                    type="button"
                    className="font-extrabold text-white underline decoration-white/70 underline-offset-2"
                    onClick={openPremium}
                  >
                    {t("events.details.boost_modal.premium_link")}
                  </button>{" "}
                  {t("events.details.boost_modal.premium_hint_after_link", {
                    days: freeDurationDays,
                  })}
                </>
              ) : (
                t(premiumHintKey, { days: freeDurationDays })
              )}
            </p>
          ) : null}

          {onSkip && !actualBoosted ? (
            <button
              type="button"
              disabled={submitting}
              aria-busy={skipSubmitting}
              className="mx-auto mt-4 flex h-11 w-full max-w-[300px] items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/[0.06] px-4 text-sm font-extrabold text-white/72 disabled:cursor-not-allowed disabled:opacity-80"
              onClick={handleSkipClick}
            >
              <span>{skipButtonLabel}</span>
            </button>
          ) : null}
    </>
  );

  if (embedded) {
    return (
      <div className="mx-auto max-w-[380px]">
        {bodyContent}
      </div>
    );
  }

  return (
    <AppModalShell
      open={isOpen}
      title={t(titleKey)}
      onClose={handleClose}
      closeAriaLabel={t("events.details.boost_modal.close")}
      maxWidthClassName="max-w-[380px]"
      desktopMaxHeight="min(90vh, 760px)"
      zIndexClassName="z-[13000]"
      bodyClassName="px-4 pb-4 pt-0"
    >
      {bodyContent}
    </AppModalShell>
  );
};

export default BoostEventModal;
